import { ConditionalLink } from "components/conditionalLink";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import ToolTip from "components/design/toolTip";
import { ActionItemCard } from "components/HomeActionItems/ActionItemCard";
import TaskDetailUnarchive from "components/icons/TaskDetailUnarchive";
import { PRICED_TICKET_CARD, TICKET_CARD } from "constants/chatType";
import { DEPRECATED, REQUESTED } from "constants/sensitiveData";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import Archive from "static/images/Archive.svg";
import NoPendingActionItems from "static/images/NoPendingActionItems.svg";
import { setMessageForStatusUpdate } from "store/slices/chat";
import { MessageResponse } from "stream-chat";
import { DefaultStreamChatGenerics, useChatContext } from "stream-chat-react";
import Async from "components/DesignSystem/AsyncComponents/Async";

type PendingTaskItemsModalProps = {
  open: boolean;
  title: string;
  ids: string[];
  onClose: () => void;
};

export const PendingTaskItemsModal = ({
  open,
  title,
  ids,
  onClose,
}: PendingTaskItemsModalProps) => {
  const { client } = useChatContext();
  const { search } = useLocation();
  const { alertToast } = useToast();
  const { isAdmin } = useRoleBasedView();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);

  const [messages, setMessages] = useState<
    MessageResponse<DefaultStreamChatGenerics>[]
  >([]);

  const idsKey = ids.join(",");

  useEffect(() => {
    if (!open || ids.length === 0) {
      setMessages([]);
      setIsLoading(false);
      return;
    }

    const getMessages = async () => {
      setIsLoading(true);
      try {
        const searchResults = await client.search(
          { members: { $in: [client.user?.id!] } },
          { id: { $in: ids } },
          {}
        );

        setMessages(
          searchResults.results
            .filter(
              (result) =>
                result.message.custom_type == TICKET_CARD ||
                result.message.custom_type == PRICED_TICKET_CARD ||
                !result.message.channel?.frozen
            )
            .map((result) => result.message)
        );
        setIsLoading(false);
      } catch (error) {
        alertToast({ message: (error as { message?: string })?.message });
      }
    };

    void getMessages();
  }, [open, idsKey]);

  return (
    <Modal.Root open={open} onOpenChange={onClose}>
      <Modal.Content useCustomOverlay>
        <Modal.Header className="sm:t-pl-4">
          <Modal.Title>{title}</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body className="t-space-y-5 sm:t-px-4">
          <Async.Root
            isLoading={isLoading}
            isSuccess={!isLoading}
            isEmpty={messages?.length === 0}
          >
            <Async.Empty>
              <div className="t-flex t-h-80 t-w-full t-flex-col t-items-center t-justify-center t-gap-8">
                <img src={NoPendingActionItems} alt="NoPendingActionItems" />
                <div className="t-text-subtitle t-text-text-30">
                  Awesome! No pending items
                </div>
              </div>
            </Async.Empty>
            <Async.Success>
              <>
                {messages.map((message) => {
                  const {
                    channel,
                    id: messageId,
                    custom_data,
                    custom_type,
                  } = message;
                  const showSeparator =
                    channel?.name && channel?.created_by?.name;

                  return (
                    <div
                      key={messageId}
                      className="t-space-y-3 t-rounded-lg t-bg-surface-lighter-grey t-p-3"
                    >
                      <div className="t-flex t-items-center t-justify-between">
                        <div className="t-flex t-items-center t-gap-1.5">
                          {message?.user?.name && (
                            <div className="t-text-subtext-sm t-text-text-100">
                              {message?.user?.name}
                            </div>
                          )}
                          {showSeparator && (
                            <div className="t-text-caption t-text-neutral-20">
                              |
                            </div>
                          )}
                          {channel?.name && (
                            <div className="t-text-body-sm t-text-text-30">
                              {channel.name}
                            </div>
                          )}
                        </div>
                        <div className="t-flex t-items-center t-gap-3">
                          {isAdmin && message.card_status === DEPRECATED && (
                            <ToolTip text="Unarchive action item">
                              <Button
                                size="small"
                                customType="ghost"
                                onClick={() =>
                                  dispatch(setMessageForStatusUpdate(message))
                                }
                              >
                                <span className="t-text-text-30">
                                  <TaskDetailUnarchive
                                    color="currentColor"
                                    strokeWidth="1"
                                  />
                                </span>
                              </Button>
                            </ToolTip>
                          )}
                          {isAdmin && message.card_status === REQUESTED && (
                            <ToolTip text="Archive action item">
                              <Button
                                size="small"
                                customType="ghost"
                                onClick={() =>
                                  dispatch(setMessageForStatusUpdate(message))
                                }
                              >
                                <img src={Archive} alt="Archive" />
                              </Button>
                            </ToolTip>
                          )}
                          {channel && (
                            <ConditionalLink
                              to={`chat/${channel.id}/${messageId}${search}`}
                              target="_blank"
                            >
                              <Button customType="link" size="small">
                                Go to chat
                              </Button>
                            </ConditionalLink>
                          )}
                        </div>
                      </div>
                      <ActionItemCard
                        type={custom_type as string}
                        customData={custom_data}
                        messageId={messageId}
                        channelId={channel?.id || ""}
                        card_status={(message?.card_status as string) || ""}
                      />
                    </div>
                  );
                })}
              </>
            </Async.Success>
          </Async.Root>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
